import { clsx, type ClassValue } from "clsx";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { EventType } from "../Components/Calendar/Calendar";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}
export const formatPhoneNumber = (number: string) => {
	if (!number) return "";
	const digits =
		number
			.replaceAll("(", "")
			.replaceAll(")", "")
			.replaceAll("-", "")
			.replaceAll(" ", "")
			.replace("+1", "")
			.match(/\d*/g)
			?.join("") ?? "";
	const matched = digits.match(/(\d{0,3})(\d{0,3})(\d{0,9})/);
	return matched ? matched.slice(1).join("-").replace(/-*$/g, "") : "";
};

export const emailSchema = z.string().email("Invalid email address");
export const phoneNumberSchema = z
	.string()
	.regex(
		/^\+?[\d\s\-()]+$/,
		"Only numbers, spaces, dashes, and brackets are allowed"
	)
	.min(8, "Phone number must be at least 8 characters long");

export const formatPhoneNumberWhileTyping = (number: string) => {
	if (!number) return "";
	const digits = number.replace(/[^\d+]/g, "");
	const hasCountryCode = digits.startsWith("+1");
	const cleanNumber = hasCountryCode ? digits.replace("+1", "") : digits;
	const matched = cleanNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
	if (!matched) return number;
	const formattedNumber = [
		matched[1] && `(${matched[1]}`,
		matched[2] && `) ${matched[2]}`,
		matched[3] && `-${matched[3]}`,
	]
		.filter(Boolean)
		.join("");
	return hasCountryCode ? `+1 ${formattedNumber}` : formattedNumber;
};

export const convertHTMLToPlainText = (html: string): string => {
	const spacedHtml = html.replace(/<\/[^>]+>/g, "$& ");
	const tempElement = document.createElement("div");
	tempElement.innerHTML = spacedHtml;
	const plainText = tempElement.textContent || tempElement.innerText || "";
	return plainText.replace(/\s+/g, " ").trim();
};

export const isHTML = (text: string) => {
	const htmlTagRegex = /<[a-z][\s\S]*>/i;
	return htmlTagRegex.test(text);
};

export const copyToClipboard = async (text: string) => {
	try {
		if (!text) throw new Error("");
		await navigator.clipboard.writeText(text);
		return true;
	} catch (error) {
		console.log(error);
		return false;
	}
};

export const areArraysEqual = (arr1: any[], arr2: any[]) => {
	if (arr1.length !== arr2.length) return false;
	return arr1.every((item, index) => item.id === arr2[index]?.id);
};

const MAX_EVENTS_PER_SLOT = 3;
export const isExceedingOverlap = (
	start: Date,
	end: Date,
	events: EventType[],
	excludeId: string
) => {
	const newStart = moment(start);
	const newEnd = moment(end);
	const interval = 15; // minutes

	for (
		let time = newStart.clone();
		time.isBefore(newEnd);
		time.add(interval, "minutes")
	) {
		let overlapCount = 1; // new event itself

		for (const event of events) {
			if (event._id === excludeId) continue;

			const eventStart = moment(event.start);
			const eventEnd = moment(event.end);

			if (time.isBetween(eventStart, eventEnd, null, "[)")) {
				overlapCount++;
			}
		}

		if (overlapCount > MAX_EVENTS_PER_SLOT) {
			return true; // this interval has too many
		}
	}

	return false;
};
